import React from 'react';
import { ArrowRight, Shield, BarChart, Globe, Network } from 'lucide-react';

const Home = () => {
  const partners = [
    { type: 'image', content: '/images/CN logo.jpg', alt: 'Canadian National Railway', className: 'h-32' },
    { type: 'image', content: '/images/Union Pacific logo.jpg', alt: 'Union Pacific', className: 'h-24' },
    { type: 'image', content: '/images/BNSF logo.jpg', alt: 'BNSF Railway', className: 'h-32' },
    { type: 'image', content: '/images/Norfolk Southern logo.png', alt: 'Norfolk Southern', className: 'h-16' },
    { type: 'image', content: '/images/CSX logo.png', alt: 'CSX Transportation', className: 'h-24' },
    { type: 'image', content: '/images/CPKC logo.png', alt: 'CPKC', className: 'h-16' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/Rail station 1.jpg"
            alt="Rail logistics management"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="hero-heading text-4xl md:text-6xl font-bold mb-6 text-white uppercase">
              Rail Logistics Management<br />Made Simple
            </h1>
            <p className="text-xl text-gray-200 mb-12 max-w-3xl mx-auto">
              Industry-leading software for rail fleet management, tracking, and logistics optimization
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-md transition-colors text-lg"
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a 
                href="/services" 
                className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-bold py-4 px-8 rounded-md transition-colors text-lg backdrop-blur-sm"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Comprehensive Rail Management Solutions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything you need to manage your rail fleet efficiently and effectively
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Fleet Management',
                description: 'Real-time tracking and management of your entire rail fleet with detailed reporting and analytics.',
                image: '/images/train image 1.jpg'
              },
              {
                icon: Network,
                title: 'EDI Integration',
                description: 'Seamless integration with Class I railroads and industry systems for automated data exchange.',
                image: '/images/Train image 2.jpg'
              },
              {
                icon: BarChart,
                title: 'Performance Analytics',
                description: 'Advanced analytics and reporting tools to optimize fleet utilization and reduce costs.',
                image: '/images/train image 3.jpg'
              },
              {
                icon: Globe,
                title: 'Network Visibility',
                description: 'Complete visibility of your rail network with real-time location and status updates.',
                image: '/images/train image 4.jpg'
              },
              {
                icon: Shield,
                title: 'Compliance Management',
                description: 'Automated compliance monitoring and reporting to meet regulatory requirements.',
                image: '/images/train imagine 3.jpg'
              },
              {
                icon: Network,
                title: 'API Integration',
                description: 'Robust API integration capabilities for seamless connection with your existing systems.',
                image: '/images/Railway 3.jpg'
              },
            ].map((feature, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-lg"
              >
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-gray-900/50 p-8 flex flex-col justify-end transform translate-y-4 group-hover:translate-y-0 transition-transform">
                  <feature.icon className="h-8 w-8 text-blue-400 mb-4" />
                  <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
                  <p className="text-gray-200 transform opacity-0 group-hover:opacity-100 transition-opacity">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section with Background Image */}
      <section className="relative py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/Railway 2.jpg"
            alt="Rail statistics"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {[
              { number: '100,000+', label: 'Railcars Managed' },
              { number: '99.9%', label: 'System Uptime' },
              { number: '30+', label: 'Years Experience' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-blue-200 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Trusted by Industry Leaders</h2>
            <p className="text-gray-600">Partnering with North America's premier rail operators</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-12">
            {partners.map((partner, index) => (
              <div 
                key={index} 
                className="flex items-center justify-center"
              >
                <img 
                  src={partner.content}
                  alt={partner.alt}
                  className={`${partner.className} object-contain`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="relative py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/Railway 2.jpg"
            alt="Rail operations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gray-900/80"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Ready to Optimize Your Rail Operations?</h2>
          <p className="text-xl mb-10 text-gray-300">Join the industry leaders who trust GeoMetrix</p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a 
              href="/contact" 
              className="inline-flex items-center bg-blue-600 text-white font-bold py-4 px-8 rounded-md hover:bg-blue-700 transition-colors text-lg"
            >
              Schedule a Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a 
              href="/services#railinc-integration" 
              className="inline-flex items-center bg-white/10 text-white font-bold py-4 px-8 rounded-md hover:bg-white/20 transition-colors text-lg backdrop-blur-sm"
            >
              View Our Solutions
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;